import {FormattedMessage, useIntl} from "react-intl";
import {Alert, Button, Col, Divider, Input, Modal, Row, Space, Typography} from "antd";
import React, {useContext, useState} from "react";
import {MedicalStaff, PortalAccessStatus} from "../../api-client";
import '../../assets/css/Dentobook.css';
import {isMobileOnly} from 'react-device-detect';
import {
    CheckCircleOutlined,
    ClockCircleOutlined,
    CloseCircleOutlined,
    CloseOutlined,
    EnvironmentOutlined,
    LockOutlined,
    MailOutlined,
    StopOutlined,
    UserOutlined,
} from "@ant-design/icons";
import UserIcon from "../common/UserIcon";
import FormButtons from "../common/FormButtons";
import parse from 'html-react-parser';
import {allCountries} from "../../i18n/countries";
import {DentobookApplicationContext} from "../../context/DentobookApplicationContext";
import {AlertContext} from "../../context/AlertContext";
import {getMedicalStaffApi} from "../../api/apiInstances";
import {ALERT_DURATIONS} from "../../constants/alerts";

const {Text} = Typography;

export interface MedicalStaffDetailsViewProps {
    medicalStaff: MedicalStaff;
    setEditMode: Function;
    setMedicalStaff?: (staff: MedicalStaff) => void;
    children?: JSX.Element | JSX.Element[];
}

const MedicalStaffDetailsView = ({medicalStaff, setEditMode, setMedicalStaff}: MedicalStaffDetailsViewProps) => {
    const {locale, principal} = useContext(DentobookApplicationContext);
    const {setSuccessMessage, setVisible, setError} = useContext(AlertContext);
    const intl = useIntl();

    const [isGrantModalOpen, setIsGrantModalOpen] = useState(false);
    const [grantEmail, setGrantEmail] = useState('');
    const [isGrantingAccess, setIsGrantingAccess] = useState(false);
    const [isRevokingAccess, setIsRevokingAccess] = useState(false);
    const [isResendingInvite, setIsResendingInvite] = useState(false);

    const isClinicAdmin = principal?.user?.role === 'CLINIC_ADMIN';

    const handleGrantAccess = async () => {
        if (!grantEmail) return;
        setIsGrantingAccess(true);
        try {
            const response = await getMedicalStaffApi().grantMedicalStaffAccess(medicalStaff.id!, {email: grantEmail});
            const updatedStaff = response.data.staff;
            setMedicalStaff?.(updatedStaff);
            setIsGrantModalOpen(false);
            setGrantEmail('');
            const msgId = updatedStaff.portalAccessStatus === PortalAccessStatus.Active ? 'access_granted' : 'invite_sent_access';
            setSuccessMessage(intl.formatMessage({id: msgId}));
            setVisible(true);
            setTimeout(() => setVisible(false), ALERT_DURATIONS.STANDARD);
        } catch (error: any) {
            if (error?.response?.status === 409) {
                const detail: string = error?.response?.data?.detail || '';
                if (detail.includes('already has active portal access')) {
                    setError(intl.formatMessage({id: 'staff_already_has_access'}));
                } else if (detail.includes('inactive')) {
                    setError(intl.formatMessage({id: 'staff_inactive_cannot_grant'}));
                } else if (detail.includes('pending invite')) {
                    // A pending invite already exists — re-fetch so the UI shows PENDING_INVITE state
                    setIsGrantModalOpen(false);
                    setGrantEmail('');
                    try {
                        const refreshed = await getMedicalStaffApi().fetchMedicalStaffById(medicalStaff.id!);
                        setMedicalStaff?.(refreshed.data);
                    } catch {
                        // ignore refresh failure; portal section will re-sync on next page visit
                    }
                    setError(intl.formatMessage({id: 'staff_already_has_pending_invite'}));
                } else if (detail.includes('linked to a different user')) {
                    setError(intl.formatMessage({id: 'staff_linked_different_user'}));
                } else {
                    setError(intl.formatMessage({id: 'unexpected_error'}));
                }
            } else {
                setError(intl.formatMessage({id: 'unexpected_error'}));
            }
        } finally {
            setIsGrantingAccess(false);
        }
    };

    const handleRevokeAccess = async () => {
        setIsRevokingAccess(true);
        try {
            const response = await getMedicalStaffApi().revokeMedicalStaffAccess(medicalStaff.id!);
            setMedicalStaff?.(response.data);
            setSuccessMessage(intl.formatMessage({id: 'access_revoked'}));
            setVisible(true);
            setTimeout(() => setVisible(false), ALERT_DURATIONS.STANDARD);
        } catch (error: any) {
            if (error?.response?.status === 409) {
                setError(intl.formatMessage({id: 'staff_no_access_to_revoke'}));
            } else {
                setError(intl.formatMessage({id: 'unexpected_error'}));
            }
        } finally {
            setIsRevokingAccess(false);
        }
    };

    const handleResendInvite = async () => {
        setIsResendingInvite(true);
        try {
            const response = await getMedicalStaffApi().resendStaffInvite(medicalStaff.id!);
            setMedicalStaff?.(response.data);
            setSuccessMessage(intl.formatMessage({id: 'invite_resent'}));
            setVisible(true);
            setTimeout(() => setVisible(false), ALERT_DURATIONS.STANDARD);
        } catch {
            setError(intl.formatMessage({id: 'unexpected_error'}));
        } finally {
            setIsResendingInvite(false);
        }
    };

    const grantModalFooter = (
        <div className="modal-btn-wrapper">
            <Button className="modal-btn" onClick={() => { setIsGrantModalOpen(false); setGrantEmail(''); }}>
                <FormattedMessage id="cancel"/>
            </Button>
            <Button className="modal-btn" type="primary" loading={isGrantingAccess} onClick={handleGrantAccess}>
                <FormattedMessage id="grant_access"/>
            </Button>
        </div>
    );

    const countryObject = allCountries.find(country => country.code === medicalStaff.country);
    const countryName = locale === 'BG' ? countryObject?.name_bg : countryObject?.name_en;

    // Use presigned R2 URL from API response
    const avatarSrc = medicalStaff.avatarUrl;

    return (
        <>
            <h4><span className="text-blue mr-8"><UserOutlined/></span> {<FormattedMessage
                id="personal_information"/>}
            </h4>
            <Space direction={isMobileOnly ? "vertical" : "horizontal"}>
                {!avatarSrc ? <UserIcon size={isMobileOnly ? "medium" : "large"}/> :
                    <img className="photo-img"
                         width={150}
                         src={avatarSrc}
                         alt="avatar"/>
                }
                <Space direction="vertical" className={isMobileOnly ? "mb-2" : "ml-56 mb-2 mt-2"}>
                    <span className="body-large-regular">{<FormattedMessage id="name"/>}:</span>
                    <span className="body-large-medium">
                        {medicalStaff.firstName + ' ' + (medicalStaff.secondName || '') + ' ' + medicalStaff.lastName}
                    </span>
                    <Space direction="vertical" className="mt-24">
                        <span className="body-large-regular">{<FormattedMessage id="role"/>}:</span>
                        <span className="body-large-medium">
                            {medicalStaff.role ? <FormattedMessage id={medicalStaff.role}/> : <FormattedMessage id="not_available"/>}
                        </span>
                    </Space>
                </Space>
                <Space direction="vertical" className={isMobileOnly ? "" : "ml-56 mb-2 mt-2"}>
                    <span className="body-large-regular">{<FormattedMessage id="uin"/>}:</span>
                    <span className="body-large-medium">
                        {medicalStaff.uin ?? <FormattedMessage id="not_available"/>}
                    </span>
                    <Space direction="vertical" className="mt-24">
                        <span className="body-large-regular">{<FormattedMessage id="specialisation"/>}:</span>
                        <span className="body-large-medium">
                            {medicalStaff.specialisation ?
                                <FormattedMessage id={medicalStaff.specialisation}/> :
                                <FormattedMessage id="not_available"/>}
                        </span>
                    </Space>
                </Space>
            </Space>
            <Divider/>
            <h4><span className="text-blue mr-8"><EnvironmentOutlined/></span> {<FormattedMessage
                id="contact"/>}
            </h4>
            <Space direction={isMobileOnly ? "vertical" : "horizontal"}>
                <Space direction={"vertical"} className="mt-3">
                    <span className="body-large-regular">{<FormattedMessage id="email"/>}:</span>
                    <span className="body-large-medium">{medicalStaff.email ? medicalStaff.email :
                        <FormattedMessage id="not_available"/>}</span>
                </Space>
                <Space direction={"vertical"} className={isMobileOnly ? "mt-3" : "mt-3 ml-56"}>
                    <span className="body-large-regular">{<FormattedMessage id="phone"/>}:</span>
                    <span className="body-large-medium">{medicalStaff.phone ? medicalStaff.phone :
                        <FormattedMessage id="not_available"/>}</span>
                </Space>
                <Space direction={"vertical"} className={isMobileOnly ? "mt-3" : "mt-3 ml-56"}>
                    <span className="body-large-regular">{<FormattedMessage id="town"/>}:</span>
                    <span className="body-large-medium">{medicalStaff.town ? medicalStaff.town :
                        <FormattedMessage id="not_available"/>}</span>
                </Space>
                <Space direction={"vertical"} className={isMobileOnly ? "mt-3" : "mt-3 ml-56"}>
                    <span className="body-large-regular">{<FormattedMessage id="country"/>}:</span>
                    <span className="body-large-medium">{medicalStaff.country ? (countryName ?? medicalStaff.country) :
                        <FormattedMessage id="not_available"/>}</span>
                </Space>
            </Space>
            <Row className="mt-32">
                <Col md={24}>
                    <Space direction={"vertical"}>
                        <span className="body-large-regular">{<FormattedMessage id="address"/>}:</span>
                        <span className="body-large-medium">
                            {medicalStaff.addressOne ? medicalStaff.addressOne : <FormattedMessage id="not_available"/>}
                        </span>
                    </Space>
                </Col>
            </Row>
            <Row className={`mt-32${isClinicAdmin ? '' : ' mb-112'}`} style={!isClinicAdmin && isMobileOnly ? {marginBottom: 150} : {}}>
                <Col md={24}>
                    <Space direction={"vertical"}>
                        <span className="body-large-regular">{<FormattedMessage id="notes"/>}:</span>
                        <span className="body-large-medium">
                            {medicalStaff.notes ? parse(medicalStaff.notes || "") :
                                <FormattedMessage id="not_available"/>}
                        </span>
                    </Space>
                </Col>
            </Row>
            {isClinicAdmin && (
                <>
                    <Divider/>
                    <h4><span className="text-blue mr-8"><LockOutlined/></span> <FormattedMessage id="portal_access"/></h4>
                    <Alert type="info" showIcon message={<FormattedMessage id="portal_access_description"/>} style={{maxWidth: 520, marginBottom: 12}}/>
                    {(() => {
                        const portalStatus = medicalStaff.portalAccessStatus ?? PortalAccessStatus.None;
                        const isPending = portalStatus === PortalAccessStatus.PendingInvite || portalStatus === PortalAccessStatus.Invited;

                        const statusNode = portalStatus === PortalAccessStatus.Active ? (
                            <span>
                                <CheckCircleOutlined style={{color: '#52c41a', marginRight: 6}}/>
                                <Text type="success"><FormattedMessage id="has_portal_access"/></Text>
                            </span>
                        ) : isPending ? (
                            <span>
                                <ClockCircleOutlined style={{color: '#fa8c16', marginRight: 6}}/>
                                <Text style={{color: '#fa8c16'}}><FormattedMessage id="invite_pending"/></Text>
                                {medicalStaff.contactEmail && (
                                    <Text type="secondary"> — {medicalStaff.contactEmail}</Text>
                                )}
                            </span>
                        ) : (
                            <span>
                                <CloseCircleOutlined style={{color: '#8c8c8c', marginRight: 6}}/>
                                <Text type="secondary"><FormattedMessage id="no_portal_access"/></Text>
                            </span>
                        );

                        const infoType = portalStatus === PortalAccessStatus.Active ? 'success' : isPending ? 'warning' : 'info';
                        const infoKey = portalStatus === PortalAccessStatus.Active
                            ? 'portal_access_info_active'
                            : isPending ? 'portal_access_info_pending' : 'portal_access_info_none';

                        return (
                            <Space direction="vertical" size="middle" className="mt-3 mb-112" style={isMobileOnly ? {marginBottom: 150, width: '100%'} : {width: '100%'}}>
                                <div>
                                    <Text strong><FormattedMessage id="status"/>:</Text>{' '}
                                    {statusNode}
                                </div>

                                <Alert
                                    type={infoType}
                                    showIcon
                                    message={<FormattedMessage id={infoKey}/>}
                                    style={{maxWidth: 520}}
                                />

                                <Space wrap>
                                    {portalStatus === PortalAccessStatus.Active && (
                                        <Button
                                            danger
                                            icon={<StopOutlined className="mr-8"/>}
                                            loading={isRevokingAccess}
                                            onClick={handleRevokeAccess}
                                        >
                                            <FormattedMessage id="revoke_access"/>
                                        </Button>
                                    )}
                                    {isPending && (
                                        <>
                                            <Button
                                                icon={<MailOutlined className="mr-8"/>}
                                                loading={isResendingInvite}
                                                onClick={handleResendInvite}
                                            >
                                                <FormattedMessage id="resend_invite"/>
                                            </Button>
                                            <Button
                                                danger
                                                icon={<StopOutlined className="mr-8"/>}
                                                loading={isRevokingAccess}
                                                onClick={handleRevokeAccess}
                                            >
                                                <FormattedMessage id="revoke_access"/>
                                            </Button>
                                        </>
                                    )}
                                    {portalStatus === PortalAccessStatus.None && (
                                        <Button
                                            type="primary"
                                            size="large"
                                            icon={<LockOutlined className="mr-8"/>}
                                            onClick={() => {
                                                setGrantEmail(medicalStaff.contactEmail || medicalStaff.email || '');
                                                setIsGrantModalOpen(true);
                                            }}
                                            disabled={!medicalStaff.isActive}
                                        >
                                            <FormattedMessage id="grant_access"/>
                                        </Button>
                                    )}
                                </Space>
                            </Space>
                        );
                    })()}
                    <Modal
                        transitionName=""
                        className="dentobook-modal"
                        title={<FormattedMessage id="grant_access"/>}
                        closeIcon={<CloseOutlined className={'icon-24px'} onClick={() => { setIsGrantModalOpen(false); setGrantEmail(''); }}/>}
                        footer={grantModalFooter}
                        open={isGrantModalOpen}
                        onCancel={() => { setIsGrantModalOpen(false); setGrantEmail(''); }}
                    >
                        <p className="body-large-regular"><FormattedMessage id="grant_access_email_label"/></p>
                        <Input
                            type="email"
                            size="large"
                            value={grantEmail}
                            onChange={(e) => setGrantEmail(e.target.value)}
                            onPressEnter={handleGrantAccess}
                        />
                    </Modal>
                </>
            )}
            <FormButtons>
                <Button onClick={() => setEditMode(true)}
                        className={"edit-btn " + (isMobileOnly ? "mobile-update-btn" : "dentobook-btn-primary")}
                        type="primary"
                        size="large" shape="round"><FormattedMessage id="edit"/></Button>
            </FormButtons>
        </>
    );
}

export default MedicalStaffDetailsView;
